import {apiClient} from '@common/http/query-client';
import {
  decryptE2eeFile,
  exportRsaPublicJwk,
  generateRsaKeyPair,
  guessMimeFromFilename,
  importMasterJwk,
  importRsaPublicJwk,
  unwrapFileKeyFromMaster,
  wrapFileKeyForRecipient,
} from '@common/e2ee/e2ee-crypto';
import {
  getMasterKeyJwk,
  getRsaPrivateJwk,
  saveRsaPrivateJwk,
  saveTrustedRsaPrivateJwk,
} from '@common/e2ee/e2ee-session';
import type {FileEntry} from '@common/uploads/file-entry';
import type {SendPackageMode} from './send-package-api';

export interface SendRecipientCapability {
  email: string;
  has_account: boolean;
  user_id?: number;
  e2ee_enabled: boolean;
  e2ee_public_key?: string | null;
}

export interface PlainCopyMapping {
  sourceEntryId: number;
  plainEntryId: number;
}

export interface E2eeDeliveryMapping {
  fileEntryId: number;
  recipientUserId: number;
  wrappedKey: string;
}

export interface PreparedSendDelivery {
  entryIds: number[];
  plainCopies: PlainCopyMapping[];
  e2eeDeliveries: E2eeDeliveryMapping[];
}

async function ensureSendRsaKeys(): Promise<void> {
  if (getRsaPrivateJwk()) {
    return;
  }

  const pair = await generateRsaKeyPair();
  const publicJwk = await exportRsaPublicJwk(pair.publicKey);
  const privateJwk = await exportRsaPrivateJwk(pair.privateKey);
  saveRsaPrivateJwk(privateJwk);
  saveTrustedRsaPrivateJwk(privateJwk);
  await apiClient.post('user/e2ee/enable', {e2ee_public_key: publicJwk});
}

export async function fetchRecipientCapabilities(
  emails: string[],
): Promise<SendRecipientCapability[]> {
  const {data} = await apiClient.post<{recipients: SendRecipientCapability[]}>(
    'send-packages/recipient-capabilities',
    {emails},
  );
  return data.recipients;
}

function recipientNeedsPlain(cap: SendRecipientCapability): boolean {
  return (
    !cap.has_account ||
    !cap.e2ee_enabled ||
    !cap.e2ee_public_key
  );
}

async function createPlainCopyFromEntry(
  entry: FileEntry,
  uploadPlainFile: (file: File) => Promise<number>,
): Promise<number> {
  const masterJwk = getMasterKeyJwk();
  if (!masterJwk || !entry.e2ee_iv || !entry.e2ee_wrapped_key) {
    throw new Error('Could not prepare plain copy for encrypted file.');
  }

  const ciphertext = await fetch(entry.url, {credentials: 'include'}).then(r => {
    if (!r.ok) {
      throw new Error('Could not download encrypted file for plain copy.');
    }
    return r.arrayBuffer();
  });

  const master = await importMasterJwk(masterJwk);
  const plain = await decryptE2eeFile(
    ciphertext,
    master,
    entry.e2ee_iv,
    entry.e2ee_wrapped_key,
  );
  const mime = guessMimeFromFilename(entry.name);
  const file = new File([plain], entry.name, {type: mime});

  return uploadPlainFile(file);
}

export async function prepareSendDelivery(options: {
  mode: SendPackageMode;
  entryIds: number[];
  recipients: string[];
  entries: FileEntry[];
  uploadPlainFile: (file: File) => Promise<number>;
}): Promise<PreparedSendDelivery> {
  const {mode, recipients, entries, uploadPlainFile} = options;
  let entryIds = [...options.entryIds];

  const capabilities =
    mode === 'share' && recipients.length
      ? await fetchRecipientCapabilities(recipients)
      : [];

  const needsPlainCopy =
    mode !== 'share' || capabilities.some(recipientNeedsPlain);

  const plainCopies: PlainCopyMapping[] = [];
  const plainEntryIdBySource = new Map<number, number>();

  if (needsPlainCopy) {
    for (const entry of entries) {
      if (entry.encryption_mode !== 'e2ee') {
        continue;
      }

      const plainEntryId = await createPlainCopyFromEntry(entry, uploadPlainFile);
      plainEntryIdBySource.set(entry.id, plainEntryId);
      plainCopies.push({
        sourceEntryId: entry.id,
        plainEntryId,
      });
    }
  }

  if (mode !== 'share') {
    entryIds = entryIds.map(id => plainEntryIdBySource.get(id) ?? id);
    return {entryIds, plainCopies: [], e2eeDeliveries: []};
  }

  const e2eeRecipients = capabilities.filter(
    cap => cap.has_account && cap.e2ee_enabled && cap.e2ee_public_key && cap.user_id,
  );

  const e2eeDeliveries: E2eeDeliveryMapping[] = [];

  if (e2eeRecipients.length) {
    const masterJwk = getMasterKeyJwk();
    if (masterJwk) {
      await ensureSendRsaKeys();
      const master = await importMasterJwk(masterJwk);

      for (const entry of entries) {
        if (entry.encryption_mode !== 'e2ee' || !entry.e2ee_wrapped_key) {
          continue;
        }

        const fileKey = await unwrapFileKeyFromMaster(
          master,
          entry.e2ee_wrapped_key,
        );

        for (const recipient of e2eeRecipients) {
          const publicKey = await importRsaPublicJwk(recipient.e2ee_public_key!);
          const wrappedKey = await wrapFileKeyForRecipient(fileKey, publicKey);
          e2eeDeliveries.push({
            fileEntryId: entry.id,
            recipientUserId: recipient.user_id!,
            wrappedKey,
          });
        }
      }
    }
  }

  return {entryIds, plainCopies, e2eeDeliveries};
}
